import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Trophy, ArrowRight, Check, Home } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const sportTypes = [
  { value: 'soccer', label: 'Soccer / Futsal' },
  { value: 'basketball', label: 'Basketball' },
  { value: 'volleyball', label: 'Volleyball' },
  { value: 'hockey', label: 'Hockey' },
  { value: 'cricket', label: 'Cricket' },
  { value: 'other', label: 'Other' }
];

export default function OnboardingPage() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [checkingOrg, setCheckingOrg] = useState(true);
  const [existingOrg, setExistingOrg] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    sportType: 'soccer',
    tournamentName: '',
    subdomain: ''
  });
  const { token, organization, setOrganization, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Check if user already has an organization
  useEffect(() => {
    const checkExistingOrg = async () => {
      // Get token from context or localStorage
      const authToken = token || localStorage.getItem('auth_token');
      
      if (!authToken) {
        setCheckingOrg(false);
        return;
      }

      // If organization is already in context, use it
      if (organization) {
        setExistingOrg(organization);
        setCheckingOrg(false);
        return;
      }

      // Otherwise, fetch from API with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${authToken}` },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const data = await response.json();
          if (data.organization) {
            setExistingOrg(data.organization);
            setOrganization(data.organization);
          }
        }
      } catch (error) {
        clearTimeout(timeoutId);
        console.error('Failed to check organization:', error);
      } finally {
        setCheckingOrg(false);
      }
    };

    // Run check when auth is done loading or immediately if we have a token in localStorage
    if (!authLoading || localStorage.getItem('auth_token')) {
      checkExistingOrg();
    }
  }, [token, organization, authLoading, setOrganization]);

  // Show loading while checking
  if (authLoading || checkingOrg) {
    return (
      <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Show "Go to Dashboard" option if user already has an organization
  if (existingOrg) {
    return (
      <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          <div className="bg-[#18181b] border border-white/10 rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <img 
                  src="/logo.jpg" 
                  alt="Score Copilot" 
                  className="h-14 w-auto object-contain"
                />
                <span className="font-heading text-2xl font-bold text-white uppercase">
                  Score Copilot
                </span>
              </div>
              <h1 className="font-heading text-3xl font-bold text-white uppercase">
                Welcome Back!
              </h1>
              <p className="text-gray-400 mt-2">You already have a tournament set up</p>
            </div>

            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-6 mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <Trophy className="text-emerald-500" size={24} />
                </div>
                <div>
                  <p className="text-white font-bold text-lg">{existingOrg.tournament_name}</p>
                  <p className="text-gray-400 text-sm">{existingOrg.name}</p>
                </div>
              </div>
            </div>

            <Button
              onClick={() => navigate('/dashboard')}
              data-testid="go-to-dashboard-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 btn-lift"
            >
              <Home className="mr-2" size={18} />
              Go to My Tournament
            </Button>

            <p className="text-center text-gray-500 text-sm mt-4">
              Or{' '}
              <Link to="/" className="text-emerald-500 hover:text-emerald-400">
                go back to homepage
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleSubdomainChange = (value) => {
    // Sanitize subdomain - only lowercase letters, numbers, hyphens
    const sanitized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setFormData({ ...formData, subdomain: sanitized });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.tournamentName || !formData.subdomain) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/organizations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          sport_type: formData.sportType,
          tournament_name: formData.tournamentName,
          subdomain: formData.subdomain
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create organization');
      }

      const org = await response.json();
      setOrganization(org);
      toast.success('Organization created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                step >= s 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-white/10 text-gray-500'
              }`}>
                {step > s ? <Check size={18} /> : s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-0.5 mx-2 ${step > s ? 'bg-emerald-500' : 'bg-white/10'}`} />
              )}
            </div>
          ))}
        </div>

        <div className="bg-[#18181b] border border-white/10 rounded-2xl p-8 shadow-2xl">
          {/* Step 1: Organization Name */}
          {step === 1 && (
            <div className="animate-fadeIn">
              <div className="text-center mb-8">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <img 
                    src="/logo.jpg" 
                    alt="Score Copilot" 
                    className="h-14 w-auto object-contain"
                  />
                  <span className="font-heading text-2xl font-bold text-white uppercase">
                    Score Copilot
                  </span>
                </div>
                <h1 className="font-heading text-3xl font-bold text-white uppercase">
                  Let&apos;s Get Started
                </h1>
                <p className="text-gray-400 mt-2">Tell us about your organization</p>
              </div>

              <div className="space-y-5">
                <div>
                  <Label className="text-gray-300">Organization Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Toronto Indoor Soccer League"
                    data-testid="org-name-input"
                    className="mt-2 bg-black/20 border-white/10 text-white placeholder:text-gray-500"
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Sport Type</Label>
                  <Select 
                    value={formData.sportType} 
                    onValueChange={(value) => setFormData({ ...formData, sportType: value })}
                  >
                    <SelectTrigger 
                      className="mt-2 bg-black/20 border-white/10 text-white"
                      data-testid="sport-type-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-white/10">
                      {sportTypes.map((sport) => (
                        <SelectItem key={sport.value} value={sport.value} className="text-white hover:bg-white/10">
                          {sport.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={() => setStep(2)}
                  disabled={!formData.name}
                  data-testid="onboarding-next-1"
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 btn-lift mt-4"
                >
                  Continue
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Tournament Name */}
          {step === 2 && (
            <div className="animate-fadeIn">
              <div className="text-center mb-8">
                <h1 className="font-heading text-3xl font-bold text-white uppercase">
                  Name Your Tournament
                </h1>
                <p className="text-gray-400 mt-2">This is what your players and fans will see</p>
              </div>

              <div className="space-y-5">
                <div>
                  <Label className="text-gray-300">Tournament Name</Label>
                  <Input
                    value={formData.tournamentName}
                    onChange={(e) => setFormData({ ...formData, tournamentName: e.target.value })}
                    placeholder="e.g., Winter Indoor Championship 2026"
                    data-testid="tournament-name-input"
                    className="mt-2 bg-black/20 border-white/10 text-white placeholder:text-gray-500"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => setStep(1)}
                    variant="outline"
                    className="flex-1 border-white/10 text-white hover:bg-white/5"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={() => setStep(3)}
                    disabled={!formData.tournamentName}
                    data-testid="onboarding-next-2"
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white btn-lift"
                  >
                    Continue
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Subdomain */}
          {step === 3 && (
            <div className="animate-fadeIn">
              <div className="text-center mb-8">
                <h1 className="font-heading text-3xl font-bold text-white uppercase">
                  Choose Your URL
                </h1>
                <p className="text-gray-400 mt-2">This will be your public tournament page</p>
              </div>

              <div className="space-y-5">
                <div>
                  <Label className="text-gray-300">Subdomain</Label>
                  <div className="mt-2 flex items-center">
                    <Input
                      value={formData.subdomain}
                      onChange={(e) => handleSubdomainChange(e.target.value)}
                      placeholder="my-league"
                      data-testid="subdomain-input"
                      className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 rounded-r-none"
                    />
                    <div className="px-4 py-2 bg-white/5 border border-l-0 border-white/10 rounded-r-md text-gray-400 text-sm whitespace-nowrap">
                      .scorecopilot.com
                    </div>
                  </div>
                  {formData.subdomain && (
                    <p className="text-emerald-500 text-sm mt-2">
                      Your tournament URL: scorecopilot.com/t/{formData.subdomain}
                    </p>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => setStep(2)}
                    variant="outline"
                    className="flex-1 border-white/10 text-white hover:bg-white/5"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={!formData.subdomain || loading}
                    data-testid="onboarding-submit"
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white btn-lift"
                  >
                    {loading ? 'Creating...' : 'Create Tournament'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
